/**
 * OrdersBulkBar - Toolbar shown when orders are checked in PortalOrdersPage.
 *
 * Currently exposes a single bulk action (Dismiss Drafts) plus a Clear
 * affordance — patterned after InboxBulkBar but scoped to orders so
 * action labels match what the user is operating on.
 */
import styles from './OrdersBulkBar.module.css';

interface OrdersBulkBarProps {
  /** Total checked across the page. */
  count: number;
  /** Of `count`, how many are draft orders (only these will be dismissed). */
  draftCount: number;
  onDismissDrafts: () => void;
  dismissing?: boolean;
  onClear: () => void;
}

const OrdersBulkBar: React.FC<OrdersBulkBarProps> = ({
  count,
  draftCount,
  onDismissDrafts,
  dismissing = false,
  onClear,
}) => {
  const dismissDisabled = dismissing || draftCount === 0;
  const dismissTitle =
    draftCount === 0
      ? 'Selection contains no draft orders to dismiss'
      : `Permanently delete ${draftCount} draft order${draftCount === 1 ? '' : 's'}`;

  return (
    <div className={styles.bar} role="toolbar" aria-label="Bulk order actions">
      <span className={styles.count}>
        {count} selected
        {draftCount !== count && (
          <span className={styles.subcount}>
            {' '}({draftCount} draft{draftCount === 1 ? '' : 's'})
          </span>
        )}
      </span>
      <div className={styles.actions}>
        <button
          className={`${styles.actionBtn} ${styles.dismissBtn}`}
          onClick={onDismissDrafts}
          disabled={dismissDisabled}
          type="button"
          title={dismissTitle}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
          {dismissing ? 'Dismissing...' : `Dismiss ${draftCount > 0 ? draftCount : ''} Draft${draftCount === 1 ? '' : 's'}`.trim()}
        </button>
        <button
          className={`${styles.actionBtn} ${styles.clearBtn}`}
          onClick={onClear}
          type="button"
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export default OrdersBulkBar;
